import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ViewChild
} from "@angular/core";
import { BehaviorSubject, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { AdvAnnouncement } from "@onsip/common/services/api/resources/announcement/adv-announcement";
import { AdvAnnouncementService } from "@onsip/common/services/api/resources/announcement/adv-announcement.service";
import { Recording } from "@onsip/common/services/api/resources/recording/recording";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { SnackbarService } from "../../../../../shared/components/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { TogglableRecorderComponent } from "@onsip/web/features/shared/components/togglableRecorder/togglable-recorder.component";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { MatDialog } from "@angular/material/dialog";

@Component({
  selector: "onsip-adv-announcements-recording-edit",
  templateUrl: "./adv-announcements-recording-edit.component.html",
  styleUrls: ["./adv-announcements-recording-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdvAnnouncementsRecordingEditComponent implements OnInit, OnDestroy {
  @Input() announcement!: AdvAnnouncement;
  @Output() viewPanel = new EventEmitter<void>();
  @Input() recording!: Recording | undefined;
  @Input() recordingArray!: Array<Recording | undefined>;

  @ViewChild(TogglableRecorderComponent) recorderRef!: TogglableRecorderComponent;

  recordingEditInfo = new FormGroup({
    record: new FormControl<Recording | undefined>(undefined, [Validators.required])
  });

  private unsubscriber = new Subscription();
  loadingStateComplete$ = new BehaviorSubject(true);

  constructor(
    private snackbar: SnackbarService,
    private announcementService: AdvAnnouncementService,
    private translate: TranslateService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.recordingEditInfo.controls.record.setValue(this.recording);
  }

  handleAnnouncementEdit(successMessage: string): void {
    this.snackbar.openSnackBar(successMessage, "success");
    this.viewPanel.emit();
  }

  submit(): void {
    if (this.recordingEditInfo.controls.record.valid) {
      this.loadingStateComplete$.next(false);
      this.recorderRef.getRecNumber().then(recNum => this.handleSelectorFile(recNum));
    } else if (this.recordingEditInfo.controls.record.hasError("required")) {
      this.snackbar.openSnackBar(
        this.translate.instant(
          "Either no files or too many files were chosen. Please select only one file."
        ),
        "error"
      );
    }
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  togglePanel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(res => {
          if (res && res.doPrimaryAction) this.viewPanel.emit();
        })
    );
  }

  private handleSelectorFile(recNumber: string): void {
    const params = {
      AdvAnnouncementId: this.announcement.advAnnouncementId,
      Name: this.announcement.name,
      RecordingNumber: recNumber,
      TransferAddressUsername: this.announcement.transferAddressUsername || ""
    };
    this.announcementService.advAnnouncementEdit(params).then(response => {
      if (response.status === "success") {
        this.handleAnnouncementEdit("Recording successfully assigned to announcement");
      } else {
        this.announcementService.clearErrors();
        this.showSaveErrorSnackbar();
      }
      this.loadingStateComplete$.next(true);
    });
  }

  private showSaveErrorSnackbar(): void {
    this.snackbar.openSnackBar(
      this.translate.instant("ONSIP_I18N.THERE_WAS_AN_ERROR_SAVING_YOUR_INFORMATION"),
      "error"
    );
  }
}
